import { isResource } from '@medplum/core'
import type {
  CareTeam,
  Coding,
  Composition,
  CompositionSection,
  Condition,
  DocumentReference,
  Immunization,
  MedicationRequest,
  Observation,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes'
import {
  hasAttemptBudget,
  isRetryDue,
  nextFailedAttemptCount,
} from '#src/aiAttemptBudget.ts'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  COMPOSITION_NOTE_AI_SUMMARY_FAILED_ATTEMPTS_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_GENERATED_AT_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_STARTED_AT_SYSTEM,
  COMPOSITION_NOTE_AI_SUMMARY_SYSTEM,
  COMPOSITION_NOTE_AI_TITLE_SYSTEM,
  COMPOSITION_NOTE_SEARCH_TEXT_SYSTEM,
  COMPOSITION_TYPE,
  NOTE_LABEL_TAG_SYSTEM,
  SEEN_HEALTH_SYSTEM,
  SIGNED_BY_SYSTEM,
  SUGGESTED_EDITS_STAGING_SYSTEM,
} from '#src/codeSystems.ts'
import { type DateString, today } from '#src/dates.ts'
import { Extension, Tag } from '#src/decorators.ts'
import { invariant } from '#src/invariant.ts'
import type { JsonLike, JsonObject } from '#src/json.ts'
import { ExtendedComposition } from '#src/models/compositions/composition.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import { withValidatedToolEntries } from '#src/models/compositions/validatedToolEmbeds.ts'
import {
  type NoteLabel,
  noteLabelTagCodings,
  readNoteLabelReferences,
} from '#src/models/noteLabel.ts'
import { decidableValidatedToolLinkIds } from '#src/models/validatedToolFields.ts'
import {
  mergeVisitNoteCategoryTokens,
  readVisitNoteCategoryTokens,
} from '#src/models/visitNoteCategory.ts'
import { isReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  isNonNegativeInteger,
  isOfType,
  now,
  parseReferenceString,
} from '#src/utils.ts'

const COMPOSITION_NOTE_ORIGIN_NODE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/composition-note-origin-node`

/**
 * A SEMANTIC editor operation persisted alongside a staged suggestion.
 *
 * `@seen/core` is a leaf package, so it must NOT import the `EditorOperation`
 * discriminated union that lives in `@seen/ai-agents`
 * (`packages/ai-agents/src/tools/editEditor.ts`). Instead this is an OPEN union:
 * the two v0-critical op shapes (`setChoiceById`, `appendToSection`) are typed
 * precisely so the substrate round-trips them losslessly, plus a tolerant escape
 * arm so every other current editEditor op (`replaceText`, `replaceNode`,
 * `insertAfterText`, `insertBeforeText`, `insertAtEnd`, `replaceAll`) and any
 * future op persists verbatim without a core-side change. `@seen/ai-agents` /
 * `@seen/react` narrow this back to the precise union on read (B2).
 *
 * The op is stored, not resolved positions: positions are doomed under
 * y-prosemirror collab/undo, so hydration re-resolves the op against the live doc.
 */
export type PersistedEditorOperation =
  | ({
      type: 'setChoiceById'
      fieldId: string
      value: string
    } & SuggestionCitation)
  | ({
      type: 'appendToSection'
      headingText: string
      content: string
    } & SuggestionCitation)
  | ({
      type: 'setTaskItemChecked'
      labelText: string
      checked: boolean
      sectionHeading?: string
    } & SuggestionCitation)
  | PersistedValidatedToolOp
  | PersistedQuestionnaireAnswerOp
  | ({ type: string } & JsonObject)

/* The operation types a revision can replace, and the field each keeps its
 * prose in. An op absent from this map holds no prose to restate — a choice
 * value, a checkbox state, or the markup of a field already in the document —
 * so a revision naming one has nothing to revise, and `replaceNode` is excluded
 * for that reason even though it has a `newContent`.
 *
 * Both sides of the revision read it, which is why it lives here: the server
 * offers only these as revisable, and the browser rewrites a revision into the
 * superseded op's own shape so the persisted record re-resolves through the
 * ordinary hydration path, which knows nothing about revisions. */
export const REVISABLE_OP_CONTENT_FIELD = {
  replaceText: 'newContent',
  insertAfterText: 'content',
  insertBeforeText: 'content',
  appendToSection: 'content',
  insertAtEnd: 'content',
  replaceAll: 'content',
} as const

// The fields of an editor operation that citation and revision logic read.
// Each is loosely typed because a model-authored or persisted record is not trusted
// to have kept the shape.
export interface OperationFields {
  readonly type?: unknown
  readonly content?: unknown
  readonly newContent?: unknown
  readonly citation?: JsonLike
  readonly source?: JsonLike
  readonly evidence?: JsonLike
  readonly sourceProvenance?: JsonLike
}

type MutableOperationFields = {
  -readonly [K in keyof OperationFields]: OperationFields[K]
}

type RevisableContentField =
  (typeof REVISABLE_OP_CONTENT_FIELD)[keyof typeof REVISABLE_OP_CONTENT_FIELD]

export function revisableContentField(
  op: OperationFields
): RevisableContentField | undefined {
  const type = op.type
  if (typeof type !== 'string') {
    return undefined
  }
  const field = Object.entries(REVISABLE_OP_CONTENT_FIELD).find(
    ([opType]) => opType === type
  )?.[1]
  if (field === undefined) {
    return undefined
  }
  return typeof op[field] === 'string' ? field : undefined
}

/* The superseded op with its prose swapped for the revision's, or undefined
 * when that op holds none. Everything else is carried verbatim — the anchor
 * above all, since a revision lands where the original sat and never re-derives
 * its own position. The caller supplies the carried citation separately; this
 * keeps whatever the original had until it does. */
export function reviseOpContent<T extends OperationFields>(
  op: T,
  content: string
): T | undefined {
  const field = revisableContentField(op)
  if (field === undefined) {
    return undefined
  }
  return { ...op, [field]: content }
}

/* The resource an AI suggestion drew from together with the words it took from
 * that resource. `reference` is `ResourceType/{id}` of one of the citable
 * types; `quote` is the model's excerpt of it.
 *
 * The reference is required here, which is the point of the pair being one
 * object: a quote standing on its own was never a citation, and while the two
 * were independent optional fields a suggestion could carry an orphan quote
 * that still rendered under a heading reading "Source excerpt". Nesting makes
 * that unrepresentable rather than something each surface has to remember to
 * reject.
 *
 * The model's own name for the source is not kept. The chip is named from the
 * resolved resource, so a label the model composed cannot drift from what the
 * notes list says. */
export type CitedSource = {
  reference: string
  quote?: string
}

/* What kind of source a suggestion drew from, so a clinician can tell a value
 * carried forward from an old document, which may be stale, from one read off
 * the live chart or said in this visit. */
export const SOURCE_KINDS = [
  'this-visit',
  'chart-record',
  'carried-forward',
] as const

export type SourceKind = (typeof SOURCE_KINDS)[number]

function isSourceKind(value: unknown): value is SourceKind {
  return SOURCE_KINDS.some((kind) => kind === value)
}

/* The kind of source a reference names. The chat thread a clinician spoke in
 * is this visit; a prior document (a visit note, a legacy assessment, a filed
 * document) is carried forward; every other resource is a live chart record,
 * including a type this does not list, so a newly citable structured record
 * defaults to current rather than to stale. */
export function sourceKindOfReference(reference: string): SourceKind {
  switch (parseReferenceString(reference)?.type) {
    case 'Communication':
      return 'this-visit'
    case 'Composition':
    case 'DocumentReference':
      return 'carried-forward'
    default:
      return 'chart-record'
  }
}

/* Where a staged suggestion's content actually came from, always filled in by
 * the server and never authored by the model. A discriminated union rather
 * than one shape with optional fields, because the kinds don't share fields
 * that mean the same thing across kinds — a `quote` on `passage` locates words
 * on a signed record, the same field on `clinician` locates words in a chat
 * turn, and neither `absence` nor `region` has a quote to give at all. */
export type PassageProvenance = {
  kind: 'passage'
  /* The signed clinical record the suggestion drew from. */
  reference: string
  /* The model's verbatim excerpt of that record. */
  quote: string
  /* The version of `reference` the quote was read at, so a later change to
   * the source can be detected against the citation that named it. */
  versionId: string
  /* Stamped by the server at staging. A record staged before this field
   * existed carries none; readers fall back to `sourceKindOfReference`. */
  sourceKind?: SourceKind
}

/* One record an absence claim actually searched, pinned to the version it was
 * read at. An absence claim is a statement about a set of records, and "the
 * set" only means anything at a version: a record that changes underneath the
 * claim leaves it asserting an absence no reader can re-check, and nothing in
 * an unpinned entry reveals that it went stale. This is the same pin
 * `PassageProvenance.versionId` puts on the record a quote was read from, for
 * the same reason — the claim and the evidence behind it have to name the same
 * version of the chart. */
export type SearchedRecord = {
  reference: string
  versionId: string
}

/* The suggestion asserts something is absent from the chart: `question` is
 * what was asked, `searched` is every record actually searched to answer it.
 * There is no separate count field — `searched.length` IS the count of
 * extractions that succeeded, and a redundant count could drift out of
 * agreement with the array it would be describing. A failed extraction did
 * not search its record, so it must never appear in `searched` or inflate its
 * length. An empty `searched` is not an error: having found nothing searchable
 * is a different claim from having searched records and found nothing in
 * them, and both are representable. */
export type AbsenceProvenance = {
  kind: 'absence'
  question: string
  searched: SearchedRecord[]
}

/* The clinician said this themselves, in chat. `reference` names the
 * `Communication` Orbit persists the whole chat thread under
 * (`AIThread extends ExtendedCommunication`, `packages/core/src/models/communications/AIThread.ts`
 * — the entire message array lives base64-JSON in a payload attachment). The
 * thread Communication has no per-message FHIR id, so the reference identifies
 * the thread and `quote`, the clinician's own words verbatim, is what locates
 * their turn within it. `messageId` is that turn's own id, carried because the
 * persisted array is `UIMessage`s and each one already has a unique id — an
 * id survives the array being trimmed or rewritten, where a positional index
 * would silently start naming a different turn.
 *
 * It is optional because a provenance written without one is still usable,
 * just addressed to the thread rather than the turn. What it buys is exactness
 * in both directions: a chip can deep-link to the turn, and the check that
 * `quote` is a real substring can run against that one message instead of the
 * whole array — where a phrase the clinician used in an unrelated earlier turn
 * would satisfy it.
 *
 * That check belongs to minting. Clinician provenance is
 * minted only when the clinician's message is the source of a genuinely new
 * assertion, and is validated then against the messages the server registered
 * as citable for that run — only the signed-in clinician's own turns, matched
 * on the author the chat stamped on each. */
export type ClinicianProvenance = {
  kind: 'clinician'
  reference: string
  quote: string
  messageId?: string
  /* `Practitioner/{id}` of the clinician who sent the message: the signed-in
   * practitioner the server matched against the message's own author stamp
   * before registering it. */
  author?: string
  /* When the message was sent, ISO 8601, as the chat stamped it. */
  sentAt?: string
  /* Present when the quote is the clinician's answer to a question the
   * assistant asked (`askUserQuestion`) rather than words they typed: the id
   * of that call. `messageId` then names the assistant message holding it, and
   * `quote` is the question and the answer given, "<question> → <answer>". */
  toolCallId?: string
  // As `PassageProvenance.sourceKind`.
  sourceKind?: SourceKind
}

/* A rectangle on a rendered page, as `[ymin, xmin, ymax, xmax]` integers on a
 * 0-1000 scale in each axis.
 *
 * Normalized rather than pixels because the page these coordinates describe is
 * re-rendered by whoever displays it, at whatever DPI that surface happens to
 * use. A pixel box is only meaningful beside the exact raster it was measured
 * on, so a re-render at a different scale would move every box; a 0-1000
 * fraction survives it.
 *
 * Integers rather than floats because a thousandth of a page is already far
 * finer than any region a clinician reads, and floats would invite
 * accumulating rounding differences between the minter, the store, and the
 * renderer for precision nobody can see.
 *
 * `[ymin, xmin, ymax, xmax]` is vision-model order, kept verbatim rather than
 * rearranged into a more familiar `x, y, w, h`. The transposition is exactly
 * the kind of change that looks harmless and silently swaps the axes of every
 * box; the order is named once here so no call site has to guess.
 *
 * A named type rather than the tuple written out at each field, so the two
 * boxes on a region provenance cannot drift apart in shape or in order. */
export type NormalizedBox = [number, number, number, number]

/* The upper bound of `NormalizedBox` in both axes. The lower bound is 0. */
export const NORMALIZED_BOX_SCALE = 1000

/* Whether `inner` lies within `outer`, edges inclusive. Boundary-equal edges
 * count as contained: a value that fills its form row exactly is contained by
 * it, and a rounded coordinate landing on the edge is the common case, not an
 * error.
 *
 * Each axis is checked as one chain, `outer min <= inner min <= inner max <=
 * outer max`, so a box whose max sits before its min — on either side — is
 * contained by nothing and contains nothing. An inverted box describes no
 * drawable region, and the minter this predicate guards must not accept one.
 *
 * This is the containment invariant a region provenance must satisfy —
 * `boxContains(provenance.contextBox, provenance.payloadBox)` — kept as a
 * predicate over two plain boxes rather than as a shape the type system
 * enforces. TypeScript cannot express "inside" over number tuples, and the
 * check belongs where the provenance is MINTED: a record that already violates
 * it is a defect to surface, not one to make unreadable. Nothing mints region
 * provenance yet, so today this has no caller in the write path. */
export function boxContains(
  outer: NormalizedBox,
  inner: NormalizedBox
): boolean {
  const [outerYmin, outerXmin, outerYmax, outerXmax] = outer
  const [innerYmin, innerXmin, innerYmax, innerXmax] = inner
  return (
    outerYmin <= innerYmin &&
    innerYmin <= innerYmax &&
    innerYmax <= outerYmax &&
    outerXmin <= innerXmin &&
    innerXmin <= innerXmax &&
    innerXmax <= outerXmax
  )
}

/* The suggestion was read off a REGION OF A PAGE IMAGE in a scanned clinical
 * document — a POLST form, an advance directive — rather than out of text.
 *
 * It is a separate kind rather than a passage with coordinates bolted on
 * because the evidence is genuinely different: a passage's evidence is the
 * quote, and this has no quote to give. The stored OCR transcription of these
 * forms silently drops checkbox state, so the transcribed text of a POLST can
 * read as though no box were ticked on a form where one plainly is. The
 * evidence therefore has to be the PIXELS, and a `quote` field here would
 * invite exactly the substitution this kind exists to prevent.
 *
 * `reference` names the `DocumentReference`, `attachmentIndex` which of its
 * attachments, and `page` which page within that attachment (1-based, as a
 * clinician counts pages). `versionId` pins the document for the same reason
 * `PassageProvenance.versionId` pins a quoted record: a document re-scanned or
 * re-filed under the same reference leaves the coordinates describing a page
 * that is no longer there, and nothing in an unpinned citation reveals it. */
export type RegionProvenance = {
  kind: 'region'
  reference: string
  versionId: string
  attachmentIndex: number
  page: number
  /* The value itself: the ticked box, the handwritten name, the signature. */
  payloadBox: NormalizedBox
  /* The enclosing labelled form row, INCLUDING its printed label, and always
   * containing `payloadBox`.
   *
   * Two boxes rather than one because a tight crop of the value proves text
   * exists without saying what it means. A handwritten name on a POLST could
   * be the patient, the person who prepared the form, or the emergency
   * contact, and the crop looks identical in all three cases — only the
   * printed label beside it says which field was read. A clinician checking
   * the citation is checking that the value came from the field the suggestion
   * claims, so the label has to be inside the evidence, not asserted next to
   * it. */
  contextBox: NormalizedBox
  /* The printed label read off the form, verbatim, newlines and all (e.g.
   * `"Additional Contact\nName:"`).
   *
   * A correctness signal, not decoration. In the research spike behind this
   * type, five reads initially scored as fabrications turned out to be
   * accurate reads of a DIFFERENT printed field than the one expected, and the
   * label was the only thing that revealed it. Without it those reads are
   * indistinguishable from invention. */
  fieldLabel: string
  /* Optional `Binary/{id}` holding the rendered crop pixels.
   *
   * The pixels and the coordinates are both persisted, deliberately, because
   * they fail in opposite directions. Pixels cannot drift: a crop rendered at
   * mint time shows what the model actually looked at even if the document is
   * later re-scanned at a different size or rotation. Coordinates cannot be
   * re-derived from pixels, and they are what lets a surface re-render the
   * region over a live page at today's DPI, and what an audit reads to say
   * where on the page the claim came from. Keeping only one of the two loses a
   * property nothing else supplies. */
  crop?: string
  // As `PassageProvenance.sourceKind`.
  sourceKind?: SourceKind
}

/* Persisted on the op (and on each answer) as `sourceProvenance`, named apart
 * from `StagedSuggestion.provenance` one level up on the record. That field is
 * staging metadata — which tool call, thread, run and trace produced the
 * record, and when. This one says what the CONTENT is grounded in. They answer
 * different questions and collided only because English gave them the same
 * word; a normalizer or reader that confused them would delete a required
 * field. */
export type SuggestionProvenance =
  | PassageProvenance
  | AbsenceProvenance
  | ClinicianProvenance
  | RegionProvenance

/* What an AI suggestion carries about where it came from. It is staged on the
 * suggestion and shown beside the proposal, and accepting writes none of it
 * into the QuestionnaireResponse or the note — the citation stays on the
 * suggestion rather than entering the answer. A record staged before citations
 * existed carries none, and one staged while the model still inlined its
 * citation carries the chip in its content instead — both keep rendering as
 * they did. Records staged under the earlier flat `source`/`evidence` pair are
 * normalized into this shape when they are read, in
 * `SuggestionStagingBasic.stagingPayload`.
 *
 * `sourceProvenance` rides beside the citation for the same reason and is
 * normalized the same way — dropped when the persisted JSON doesn't pass
 * `sourceProvenanceOf`, kept otherwise. It is optional here only because a record
 * staged before provenance existed carries none; going forward the server fills
 * it on every suggestion.
 *
 * It is a list because one suggestion may rest on several passages — a short
 * narrative drawn from more than one section or record — each of them one
 * passage, in the order the model cited them. An absence claim is the one
 * entry that never shares the list: "nothing in the chart says this" and a
 * quote from the chart saying it cannot both be the grounds for one value.
 * Records staged while provenance was a single object read back as a one-entry
 * list. `citation` stays single and names the first chart passage, for readers
 * (and older deployed clients) that predate the list and fall back to it. */
export interface SuggestionCitation {
  citation?: CitedSource
  sourceProvenance?: SuggestionProvenance[]
}

/* The only resource types a suggestion may cite. Anything else is not a
 * clinical source a clinician can open from a chip, so a citation naming one
 * is rejected before it is staged. The structured kinds (care team, vitals,
 * problem list, medications, orders, immunizations, demographics) are cited
 * through server-built atoms — one line of text the server composes from the
 * record — never through a quote the model wrote. */
export const CITABLE_SOURCE_TYPES = [
  'Composition',
  'QuestionnaireResponse',
  'DocumentReference',
  'CareTeam',
  'Observation',
  'Condition',
  'MedicationRequest',
  'ServiceRequest',
  'Immunization',
  'Patient',
] as const

export type CitableSourceResource =
  | Composition
  | QuestionnaireResponse
  | DocumentReference
  | CareTeam
  | Observation
  | Condition
  | MedicationRequest
  | ServiceRequest
  | Immunization
  | Patient

export function isCitableSourceReference(
  reference: JsonLike | undefined
): reference is string {
  return citesOneOf(reference, CITABLE_SOURCE_TYPES)
}

/* The subset a model may cite by naming the reference and writing the quote
 * itself, as the pre-evidence-handles `source` field does. A structured record
 * has no prose to quote from, so it is citable only through a server-built
 * atom. */
export const DOCUMENT_SOURCE_TYPES = [
  'Composition',
  'QuestionnaireResponse',
  'DocumentReference',
] as const satisfies readonly (typeof CITABLE_SOURCE_TYPES)[number][]

export function isDocumentSourceReference(
  reference: unknown
): reference is string {
  return (
    isReferenceString(reference) && citesOneOf(reference, DOCUMENT_SOURCE_TYPES)
  )
}

function citesOneOf(
  reference: JsonLike | undefined,
  types: readonly string[]
): reference is string {
  if (!isReferenceString(reference)) {
    return false
  }
  const type = parseReferenceString(reference)?.type
  return types.some((citable) => citable === type)
}

/* Whether a cited resource has been signed, which is the second half of what
 * makes a citation usable: `isCitableSourceReference` says the reference names
 * a kind of clinical source, and this says the source is finished.
 *
 * Only a signed source may stand as evidence behind a proposal. The note tools
 * list a participant's unsigned drafts on purpose — a clinician asking which of
 * their notes is unsigned deserves an answer — so nothing upstream guarantees
 * the model was handed a finished record, and a proposal citing a colleague's
 * work in progress would read as though it cited the chart.
 *
 * Signature is NOT `Composition.status`. An Orbit-native note is signed when it
 * carries an attester and an Elation note when it carries the signed-by
 * extension, and both routinely sit at `preliminary` while signed — a status
 * check would call a signed note unsigned. A questionnaire response is finished
 * when it is `completed`; a document when a clinician has marked it `final`.
 *
 * Structured records carry no signature, so "finished" is their own status: an
 * observation that has a final result, a care team that is in force, and a
 * condition, prescription or order that was committed to the chart rather than
 * drafted or entered in error (a condition ruled out is not a finding either),
 * a vaccine that was actually given, and a participant record that has not been
 * merged into another. */
export function isSignedCitableSource(
  resource: CitableSourceResource
): boolean {
  switch (resource.resourceType) {
    case 'Composition':
      return (
        !!resource.attester?.length ||
        !!resource.extension?.some(
          (extension) => extension.url === SIGNED_BY_SYSTEM
        )
      )
    case 'QuestionnaireResponse':
      return resource.status === 'completed'
    case 'DocumentReference':
      return resource.docStatus === 'final'
    case 'CareTeam':
      return resource.status === 'active'
    case 'Observation':
      return FINISHED_OBSERVATION_STATUSES.has(resource.status)
    case 'Condition':
      return !resource.verificationStatus?.coding?.some(
        (coding) =>
          coding.code === 'entered-in-error' || coding.code === 'refuted'
      )
    case 'MedicationRequest':
    case 'ServiceRequest':
      return (
        resource.status !== 'draft' && resource.status !== 'entered-in-error'
      )
    case 'Immunization':
      return resource.status === 'completed'
    case 'Patient':
      return !resource.link?.some((link) => link.type === 'replaced-by')
  }
}

const FINISHED_OBSERVATION_STATUSES = new Set<Observation['status']>([
  'final',
  'amended',
  'corrected',
])

/* Read one field off a value that may be anything at all. The staged payload is
 * JSON nobody here wrote, so a citation can be a string, a null, or an object
 * missing the field — all of which have to answer "no field" rather than throw. */
type CitationField =
  | string
  | number
  | boolean
  | null
  | CitationField[]
  | { [key: string]: CitationField }
  | undefined

function readCitationField(
  value: JsonLike | undefined,
  key: string
): CitationField {
  return typeof value === 'object' && value !== null && key in value
    ? (value as Record<string, CitationField>)[key]
    : undefined
}

/* The citation a reference and a quote amount to, or nothing when the reference
 * is not one a chip could open. A quote that is absent or blank is dropped
 * rather than stored: a whitespace-only quote still paints the "Source excerpt"
 * heading over an empty blockquote. */
function citationFrom(
  reference: JsonLike | undefined,
  quote: JsonLike | undefined
): CitedSource | undefined {
  if (!isCitableSourceReference(reference)) {
    return undefined
  }
  return {
    reference,
    ...(typeof quote === 'string' && quote.trim().length > 0 && { quote }),
  }
}

/* One persisted `searched` entry rebuilt as a `SearchedRecord`, or nothing when
 * it does not name a citable record at a version. Built fresh rather than
 * passed through, so an extra field on the persisted entry cannot ride along
 * — the same rule the provenance read follows for the union's own fields. */
function searchedRecordFrom(
  entry: JsonLike | undefined
): SearchedRecord | undefined {
  const reference = readCitationField(entry, 'reference')
  const versionId = readCitationField(entry, 'versionId')
  if (
    !isCitableSourceReference(reference) ||
    typeof versionId !== 'string' ||
    versionId.trim().length === 0
  ) {
    return undefined
  }
  return { reference, versionId }
}

/* One persisted box rebuilt as a `NormalizedBox`, or nothing when it is not
 * four in-range integers. Rebuilt into a fresh tuple rather than passed
 * through, so a persisted five-element array cannot ride into a type that
 * claims four.
 *
 * Shape and range only. The ORDER of the coordinates is not checked — a box
 * whose `ymax` sits above its `ymin` is well-formed and wrong, and so is one
 * whose payload escapes its context — because those are mint-time invariants,
 * and `boxContains` checks both. Dropping such a record on read
 * would make a minting defect invisible to the surface best placed to show it,
 * which is the opposite of what the read tolerance is for: carrying old and
 * corrupted records, not concealing bad writes. */
function isBoxCoordinate(value: unknown): value is number {
  return isNonNegativeInteger(value) && value <= NORMALIZED_BOX_SCALE
}

function normalizedBoxFrom(
  value: JsonLike | undefined
): NormalizedBox | undefined {
  if (!Array.isArray(value) || value.length !== 4) {
    return undefined
  }
  const coordinates: unknown[] = value
  if (!coordinates.every(isBoxCoordinate)) {
    return undefined
  }
  const [ymin, xmin, ymax, xmax] = coordinates
  return [ymin, xmin, ymax, xmax]
}

/* A tolerant narrowing read of `sourceProvenance` off a staged op or answer, or
 * undefined when nothing about it holds up. The persisted payload is JSON this
 * reader did not write, so every field may be missing, null, or the wrong type
 * — none of which may throw. Each entry that survives is a freshly built object
 * carrying only the union's own fields, so an unknown extra field on the
 * persisted JSON cannot ride through.
 *
 * Both persisted shapes read the same: the list the server writes now, and the
 * single object records were staged with before a suggestion could cite more
 * than one passage, which reads back as a one-entry list. Each entry is judged
 * on its own and a malformed one is dropped alone — the passages beside it are
 * still what the suggestion rests on, and losing one understates the grounds
 * rather than inventing any. A list left with nothing is no provenance at all.
 * More than the writer's cap is kept: the cap is the writer's rule, and a
 * reader that truncated would silently pick which grounds to forget.
 *
 * An absence entry beside anything else makes the whole list unreadable. An
 * absence claim says the chart holds nothing for the question, so a list that
 * also quotes the chart contradicts itself, and no reading of it can say which
 * half the model meant; showing either half would present a claim nothing
 * vouches for. The check runs on the entries that survived, so a mangled
 * passage the writer never meant does not condemn a well-formed absence.
 *
 * Within an entry, any failing field drops the entry rather than yielding a
 * half-built one, with three exemptions, all because the entry is still usable
 * without them: a malformed `messageId`, `author`, `sentAt`, `toolCallId` or
 * `sourceKind` drops just that field, so does a malformed `crop` (the coordinates beside it
 * still locate the region), and a malformed `searched` entry is filtered out
 * rather than failing the entry. A `searched`
 * entry is malformed either way it can be: a reference no chip could open, or
 * a missing or blank `versionId`. Both drop that searched record alone, and
 * both are the same exemption rather than a new one — an unpinned record names
 * one the claim can no longer be re-checked against, which is the whole of
 * what `searched` is for, so it is no more usable than an unopenable
 * reference. The filter can only ever shorten `searched`, and since
 * `searched.length` is what an absence claim is read against, a corrupted
 * record understates the search rather than inventing one that did not happen.
 *
 * Named for the field it reads rather than for the concept, because
 * `provenanceOf(record)` would be a plausible-looking way to reach
 * `StagedSuggestion.provenance` — rebuilding, at the call site, the exact
 * conflation the field's own name was chosen to prevent.
 *
 * The tolerance is read-side only. Minting is strict: the server must never
 * write an entry this would drop, a list this would reject, nor a malformed
 * `messageId`, `author`, `sentAt` or `toolCallId`, or an unpinned `searched`
 * record this would silently drop.
 * Otherwise the tolerance stops being a compatibility property for old or
 * corrupted records and becomes an escape hatch the writer can take, which is
 * how graceful degradation turns into nothing ever being addressed precisely.
 *
 * `passage` and `absence` route their reference(s) through
 * `isCitableSourceReference`, which deliberately excludes `Communication` — a
 * chat thread is not a clinical record a chip can open as a source or a
 * searched record. `clinician` goes the other way: it must name a
 * `Communication` and is checked with the plain reference-string parser
 * instead, never `isCitableSourceReference`. `region` is the narrowest of the
 * three: citable AND a `DocumentReference`, since a page of a scanned document
 * is the only thing its coordinates can describe. */
export function sourceProvenanceOf(
  op: OperationFields
): SuggestionProvenance[] | undefined {
  const raw = op.sourceProvenance
  const entries = (Array.isArray(raw) ? raw : [raw]).flatMap(
    (entry) => sourceProvenanceEntryOf(entry) ?? []
  )
  if (entries.length === 0) {
    return undefined
  }
  if (entries.length > 1 && entries.some((entry) => entry.kind === 'absence')) {
    return undefined
  }
  return entries
}

/* One persisted provenance entry rebuilt, or nothing when it does not hold up.
 * The per-entry half of `sourceProvenanceOf`, which owns the rules. */
function sourceProvenanceEntryOf(
  raw: JsonLike | undefined
): SuggestionProvenance | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined
  }
  const {
    kind,
    quote,
    versionId,
    question,
    searched,
    messageId,
    attachmentIndex,
    page,
    payloadBox,
    contextBox,
    fieldLabel,
    crop,
    author,
    sentAt,
    toolCallId,
  } = raw as JsonObject
  // Unvalidated JSON until a guard below narrows it.
  const reference = readCitationField(raw, 'reference')
  const sourceKind: unknown = readCitationField(raw, 'sourceKind')
  // An unrecognised kind drops the field, never the entry it annotates.
  const kindField = isSourceKind(sourceKind) ? { sourceKind } : {}

  if (kind === 'passage') {
    if (
      !isCitableSourceReference(reference) ||
      typeof quote !== 'string' ||
      quote.trim().length === 0 ||
      typeof versionId !== 'string' ||
      versionId.trim().length === 0
    ) {
      return undefined
    }
    return { kind: 'passage', reference, quote, versionId, ...kindField }
  }

  if (kind === 'absence') {
    if (
      typeof question !== 'string' ||
      question.trim().length === 0 ||
      !Array.isArray(searched)
    ) {
      return undefined
    }
    return {
      kind: 'absence',
      question,
      searched: searched.flatMap((entry) => searchedRecordFrom(entry) ?? []),
    }
  }

  if (kind === 'clinician') {
    if (
      !isReferenceString(reference) ||
      parseReferenceString(reference)?.type !== 'Communication' ||
      typeof quote !== 'string' ||
      quote.trim().length === 0
    ) {
      return undefined
    }
    /* A malformed `messageId` drops the field rather than the provenance. The
     * id was validated where it was minted, so a mangled one here degrades the
     * record to thread-level addressing — exactly where a record written
     * without one already sits — rather than discarding a quote that is still
     * the clinician's own words. `author`, `sentAt` and `toolCallId` take the
     * same exemption: each only annotates the quote, so a mangled one costs
     * the chip its attribution, its timestamp or its "answer" label, never the
     * words themselves. */
    return {
      kind: 'clinician',
      reference,
      quote,
      ...(typeof messageId === 'string' &&
        messageId.trim().length > 0 && { messageId }),
      ...(isReferenceString(author) &&
        parseReferenceString(author)?.type === 'Practitioner' && { author }),
      ...(typeof sentAt === 'string' &&
        sentAt.trim().length > 0 &&
        !Number.isNaN(Date.parse(sentAt)) && { sentAt }),
      ...(typeof toolCallId === 'string' &&
        toolCallId.trim().length > 0 && { toolCallId }),
      ...kindField,
    }
  }

  if (kind === 'region') {
    const payload = normalizedBoxFrom(payloadBox)
    const context = normalizedBoxFrom(contextBox)
    if (
      /* A region is read off a scanned document, so unlike `passage` it must
       * name a `DocumentReference` specifically and not merely a citable type.
       * `attachmentIndex` and `page` address a place inside that document;
       * neither means anything on a Composition or a QuestionnaireResponse,
       * which have no attachments to index. */
      !isCitableSourceReference(reference) ||
      parseReferenceString(reference)?.type !== 'DocumentReference' ||
      typeof versionId !== 'string' ||
      versionId.trim().length === 0 ||
      !isNonNegativeInteger(attachmentIndex) ||
      /* Pages are 1-based, as a clinician counts them off a printed form. A
       * persisted 0 is a minter that forgot, not page one. */
      !isNonNegativeInteger(page) ||
      page < 1 ||
      !payload ||
      !context ||
      typeof fieldLabel !== 'string' ||
      fieldLabel.trim().length === 0
    ) {
      return undefined
    }
    /* A malformed `crop` drops the field rather than the provenance, the same
     * exemption `messageId` takes above and for the same reason: the record is
     * still usable without it. The coordinates are what locate the region, and
     * a surface can re-render the crop from them; the persisted pixels are the
     * durable copy, not the only one. Losing them leaves a citation that still
     * points at the right part of the right page. */
    return {
      kind: 'region',
      reference,
      versionId,
      attachmentIndex,
      page,
      payloadBox: payload,
      contextBox: context,
      fieldLabel,
      ...(isReferenceString(crop) &&
        parseReferenceString(crop)?.type === 'Binary' && { crop }),
      ...kindField,
    }
  }

  return undefined
}

/* Put one object's citation into the shape the surfaces read, whichever shape it
 * arrived in.
 *
 * Records staged before the citation was one object carry the two fields it
 * replaced: a `source` object naming the resource beside a separate `evidence`
 * string quoting it. Those are folded into the nested `citation` and deleted, so
 * nothing downstream has to know the old shape and no write puts them back.
 *
 * Both shapes go through the same gate rather than the old one alone. A nested
 * citation reaching a reader was written by something, and a reader that trusts
 * whatever it finds is one bad writer away from the failure this shape exists to
 * remove — an uncitable reference the clinician cannot open, or a blank quote
 * under a heading claiming an excerpt.
 *
 * The two parts of a citation stand or fall together. A quote whose source is
 * missing, or whose reference names a type no chip can open, is dropped along
 * with it: an excerpt left behind by a discarded citation still renders under a
 * heading reading "Source excerpt", so the answer would keep the appearance of
 * being quoted from the record while naming nothing.
 */
function normalizeCitation(target: MutableOperationFields): void {
  const hasCitationKey = 'citation' in target
  const hasFlat = 'source' in target || 'evidence' in target
  if (!hasCitationKey && !hasFlat) {
    return
  }
  const { source, evidence, citation: nested } = target
  delete target.source
  delete target.evidence
  /* A record already carrying the nested shape wins outright: flat fields
   * sitting beside it are residue from whatever wrote it, and say nothing
   * newer.
   *
   * A `null` is not carrying it. The schemas spell an absent optional field as
   * nullable, so a null citation is one that was never written — and the flat
   * fields beside it are then the only citation the record has, rather than
   * residue to discard. */
  const citation =
    nested === undefined || nested === null
      ? citationFrom(readCitationField(source, 'reference'), evidence)
      : citationFrom(
          readCitationField(nested, 'reference'),
          readCitationField(nested, 'quote')
        )
  if (citation) {
    target.citation = citation
  } else {
    delete target.citation
  }
}

/* `normalizeCitation`'s sibling for provenance: write back the normalized
 * list, or delete the key when `sourceProvenanceOf` rejects it. Same
 * tolerance, same in-place mutation, same reason — a record reads back the
 * same regardless of which shape reached it, and a rejected provenance left
 * in place would let a suggestion carry unverifiable "where this came from"
 * data. Writing back the list is also what retires the single-object shape: a
 * record rewritten after a read carries the list. */
function normalizeSourceProvenance(target: MutableOperationFields): void {
  const provenance = sourceProvenanceOf(target)
  if (provenance) {
    target.sourceProvenance = provenance
  } else {
    delete target.sourceProvenance
  }
}

/* Normalize every citation and provenance one staged record can carry: the op
 * itself (the `appendToSection` arm cites) and each of its answers (the
 * validated-tool and questionnaire arms cite per answer). Structural rather
 * than type-driven, because `op` is an open union: an op type core does not
 * model carries its citation through the escape arm and has to normalize the
 * same way. Tolerant of anything in the parsed payload, which is JSON this
 * reader did not write. */
function normalizeRecordCitations(record: JsonLike | undefined): void {
  if (typeof record !== 'object' || record === null) {
    return
  }
  const op = (record as { op?: unknown }).op
  if (typeof op !== 'object' || op === null) {
    return
  }
  normalizeCitation(op as MutableOperationFields)
  normalizeSourceProvenance(op as MutableOperationFields)
  const answers = (op as { answers?: unknown }).answers
  if (!Array.isArray(answers)) {
    return
  }
  for (const answer of answers) {
    if (typeof answer === 'object' && answer !== null) {
      normalizeCitation(answer as MutableOperationFields)
      normalizeSourceProvenance(answer as MutableOperationFields)
    }
  }
}

/* Normalize every citation in a parsed staging payload, in place.
 *
 * Both readers of this payload call it, so a record reads back the same whether
 * it came off the Composition extension (`CompositionNote.suggestedEditsStaging`)
 * or the per-practitioner staging `Basic`
 * (`SuggestionStagingBasic.stagingPayload`). Normalizing at read time is also
 * what retires the old shape: each setter re-serializes what its getter
 * returned, so a record rewritten after a read no longer carries the flat
 * fields. There is no backfill — these records are ephemeral per-practitioner
 * staging and age out on their own.
 */
export function normalizeStagedCitations(staging: SuggestedEditsStaging): void {
  for (const record of staging.suggestions) {
    normalizeRecordCitations(record)
  }
  /* Heal a corrupt superseded list here rather than in one reader, so both
   * agree about the same bytes. Dropping a non-array lets the next write
   * rewrite a clean sibling, where walking it would throw into the getter's own
   * catch and read a perfectly good `suggestions` array as no staging at all. */
  if (
    staging.supersededSuggestions !== undefined &&
    !Array.isArray(staging.supersededSuggestions)
  ) {
    staging.supersededSuggestions = undefined
  }
  for (const record of staging.supersededSuggestions ?? []) {
    normalizeRecordCitations(record)
  }
}

/**
 * A staged validated-tool suggestion (e.g. Braden) persisted as a unified
 * suggestion record. Unlike the inline editor ops it is NOT re-resolved into an
 * editor decoration at hydration — the validated-tool node view reads it
 * straight off the note and surfaces the banner/prefill UX. Typed precisely
 * (not via the escape arm) because the node view needs safe access to
 * `toolName`/`answers` when rendering and prefiling the form.
 */
export interface PersistedValidatedToolOp {
  type: 'proposeValidatedTool'
  /** Validated tool name, lowercase, e.g. `"braden"`. */
  toolName: string
  /** The exact form enum strings the tool's form expects, keyed by linkId. */
  /* Each answer carries the same citation a questionnaire atom does, per
   * subscale: a subscale is decided on its own, so the source behind it is too.
   * Absent on records staged before validated-tool answers could cite
   * anything. */
  answers: ({
    linkId: string
    value: string
  } & SuggestionCitation)[]
  totalScore?: number
  riskLevel?: string
}

/**
 * Narrow a persisted op to the validated-tool arm. A type-guard (rather than a
 * bare `op.type` check) because the open union's escape arm overlaps the
 * precise arms, so property access off a raw discriminant check is unsound.
 */
export function isValidatedToolOp(
  op: PersistedEditorOperation
): op is PersistedValidatedToolOp {
  return op.type === 'proposeValidatedTool' && Array.isArray(op.answers)
}

/**
 * A staged set of suggested answers for one care-plan demographic questionnaire
 * (Evaluation, Physical Functioning, Diet), persisted as a unified suggestion
 * record. Like the validated-tool op it is never re-resolved into an editor
 * decoration — the care plan's questionnaire block reads the pending record and
 * renders its own accept UI.
 *
 * Scope note: the record is staged against the CarePlan (the staging `Basic` is
 * keyed by `resourceRef`), but accepting writes to the patient-scoped
 * QuestionnaireResponse for `questionnaireUrl`. The two scopes differ by design.
 *
 * `valueString` carries HTML: the string/text items render through a rich-text
 * editor, so their stored answers are HTML strings.
 */
export interface PersistedQuestionnaireAnswerOp {
  type: 'proposeQuestionnaireAnswers'
  /** Canonical questionnaire URL, e.g. the Evaluation questionnaire's `url`. */
  questionnaireUrl: string
  answers: ({
    linkId: string
    /* The bold sub-heading `label` sits under, copied verbatim from the current
     * answer (e.g. `'OT'`, `'Home care services:'`). Present only when the item
     * repeats a label under several headings — a unique label needs no
     * qualifier, so most atoms carry none. */
    section?: string
    /* The scaffold line this value belongs to, copied verbatim from the current
     * answer including its trailing colon (e.g. `'TUG:'`). Absent when the item
     * is free prose with no scaffold, in which case the entry is the whole
     * answer — which is also how every record staged before this field existed
     * keeps behaving. */
    label?: string
    valueString?: string
    valueBoolean?: boolean
    /* Staged by a call the server held to the fill-only rule. Kept on the
     * answer rather than the record, because a superseded record's undecided
     * answers are carried into the next one. An accept of the value as
     * proposed then checks again that it only fills the answer it writes
     * over, which a clinician may have filled in since. Absent on every
     * record staged before this field existed, and on any call made without
     * the rule. */
    fillOnly?: true
  } & SuggestionCitation)[]
}

/**
 * Narrow a persisted op to the questionnaire-answer arm. A type-guard (rather
 * than a bare `op.type` check) because the open union's escape arm overlaps the
 * precise arms, so property access off a raw discriminant check is unsound.
 */
export function isQuestionnaireAnswerOp(
  op: PersistedEditorOperation
): op is PersistedQuestionnaireAnswerOp {
  return op.type === 'proposeQuestionnaireAnswers' && Array.isArray(op.answers)
}

/* Identifies one atomic answer edit within a staged questionnaire record: a
 * scaffolded answer contributes one atom per label, free prose one atom per
 * item. This is the key every `fieldDecisions` read/write for a questionnaire
 * record uses, so both unqualified forms produce exactly the key they always
 * did and nothing already staged or decided is orphaned. `section` qualifies a
 * label repeated under several sub-headings, so it is meaningless without one
 * and is dropped when `label` is absent. */
export function questionnaireAnswerKey(answer: {
  linkId: string
  section?: string
  label?: string
}): string {
  if (!answer.label) {
    return answer.linkId
  }
  return answer.section
    ? `${answer.linkId}#${answer.section}#${answer.label}`
    : `${answer.linkId}#${answer.label}`
}

/** A clinician's accept/reject decision for a single validated-tool field. */
/* `edited` is an accepted validated-tool field the clinician then changed to a
 * different answer. Their answer is kept in `StagedSuggestion.fieldEdits`; the
 * AI's is left in `op`, so the pair survives. It counts as decided wherever
 * `accepted` does. Questionnaire atoms never carry it. */
export type FieldDecisionStatus = 'pending' | 'accepted' | 'rejected' | 'edited'

/**
 * The accept receipt (design §O.4 R1): persisted on the staging record BEFORE
 * any accept side effect runs, so a crash-and-retry resumes rather than
 * re-fires. `nodeIds` are the stamp identities of the custom nodes the accept
 * inserted (`${suggestionId}:${ordinal}`); `createdRefs` maps each to the FHIR
 * resource its family's `applyOnAccept` created, filled in as they materialize.
 * Empty `nodeIds` (the common case — a plain-content accept inserts no custom
 * node) still records a receipt so the entry check can resume. The receipt's
 * refs are eventually-consistent and are NOT the source of truth for
 * compensation, which queries FHIR by stamp identity (R5, M4 blind-spot).
 */
export interface AcceptReceipt {
  nodeIds: string[]
  createdRefs?: Record<string, string>
}

/**
 * A single AI-staged inline editor suggestion persisted on a note for clinician
 * review. The `op` is the semantic editor operation (re-resolved into a live
 * proposal at hydration); `provenance` ties it back to the originating tool call
 * so the editor can clear/re-stage by tool call.
 */
export interface StagedSuggestion {
  id: string
  status: 'pending' | 'accepted' | 'rejected'
  op: PersistedEditorOperation
  provenance: {
    toolCallId: string
    threadId?: string
    runId?: string
    traceId?: string
    stagedAt: string
  }
  label?: string
  /**
   * The accept receipt (R1), present once this suggestion's accept has recorded
   * it (before side effects). Its presence is the resumable-from-either-side
   * signal alongside the in-doc stamp: the entry check reads it to skip a
   * re-insert / re-fire. Absent on a never-accepted (pending) record.
   */
  receipt?: AcceptReceipt
  /**
   * DURABLE per-field accept/reject decisions for a validated-tool suggestion,
   * keyed by answer `linkId`. Lives beside `op` (NOT inside `op.answers[]`) so
   * the AI's original proposal stays untouched — mirroring how `editedContent`
   * is a sibling annotation, not an op mutation. Absent or missing-key linkIds
   * read as `pending`, so a record staged before this field existed (or never
   * decided per-field) behaves as all-pending. Only meaningful for
   * validated-tool records; inline-suggestion records leave it undefined.
   */
  fieldDecisions?: Record<string, FieldDecisionStatus>
  /* The clinician's own answer for each field whose decision is `edited`, keyed
   * like `fieldDecisions`. A sibling annotation like `editedContent`: the AI's
   * answer in `op.answers[]` is never overwritten, so the (AI, clinician) pair
   * is retained for training. While a record is staged the form's draft store
   * is off, so this is the only place a correction to an accepted field
   * survives a reload. Present exactly for the `edited` keys. */
  fieldEdits?: Record<string, string>
  /**
   * The clinician's island-edited content for an inline suggestion (design
   * §2.b), captured when they edit a pending suggestion before accepting. A
   * SIBLING annotation beside `op` — the AI's original proposal (`op.content`)
   * stays untouched, so the (op, editedContent) PAIR is retained for AI prompt
   * tuning. Absent ⇒ never edited; accept uses `op.content`.
   */
  editedContent?: string
  decidedAt?: string
  /**
   * Set when a later AI run superseded this record and moved it into
   * `SuggestedEditsStaging.supersededSuggestions` for durable training data.
   * `supersededByRunId` is the runId of the batch that replaced it (absent when
   * that batch carried no runId). Write-once provenance stamps — a LIVE record
   * (one still in `suggestions`) never carries them.
   */
  supersededAt?: string
  supersededByRunId?: string
}

/** The full staging payload persisted in the suggested-edits-staging extension. */
export interface SuggestedEditsStaging {
  suggestions: StagedSuggestion[]
  generatedAt?: string
  /**
   * Records dropped from `suggestions` by a later AI run, preserved durably for
   * AI prompt-tuning (the original op plus any clinician decision/edits). A
   * SEPARATE array — never merged back into `suggestions` — so live-suggestion
   * consumers never surface them. Backward compatible: an older client parsing
   * this payload ignores the unknown key (plain `JSON.parse`, no strict schema),
   * and writing through the model round-trips it untouched.
   */
  supersededSuggestions?: StagedSuggestion[]
}

/* How many decisions a clinician still owes across pending staged records. A
 * record with a decidable answer set — a questionnaire, or a validated tool —
 * contributes one unit per UNDECIDED answer, not one unit for the record: each
 * answer is accepted or rejected on its own, so a partly-decided record still
 * shows the work that remains. Every other op is decided as a whole and
 * contributes one.
 *
 * A validated-tool answer naming a field its form does not have is not counted
 * at all (`decidableValidatedToolLinkIds`). The model writes linkIds as free
 * strings, so it can and does miss one — `friction-shear` for the Braden's
 * `friction-and-shear` — and such an answer renders no control anywhere. Counted
 * as outstanding work it held the banner above zero with nothing to click; the
 * count is what is DECIDABLE, never what was staged. */
export function pendingDecisionCount(
  suggestions: readonly StagedSuggestion[]
): number {
  return suggestions
    .filter((suggestion) => suggestion.status === 'pending')
    .reduce((total, suggestion) => {
      const fieldKeys = isQuestionnaireAnswerOp(suggestion.op)
        ? suggestion.op.answers.map(questionnaireAnswerKey)
        : isValidatedToolOp(suggestion.op)
          ? decidableValidatedToolLinkIds(suggestion.op)
          : undefined
      if (!fieldKeys) {
        return total + 1
      }
      const undecided = fieldKeys.filter((fieldKey) => {
        const decision = suggestion.fieldDecisions?.[fieldKey]
        return decision === undefined || decision === 'pending'
      })
      return total + undecided.length
    }, 0)
}

export interface CompositionNoteListShard {
  query: QueryTypes
  minInclusive?: DateString
  maxExclusive?: DateString
}

export class CompositionNote extends ExtendedComposition {
  static isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      hasCodeableConcept(value.category, CompositionCategory.Note)
    )
  }

  constructor({
    content,
    author,
    originNode,
    labels,
    deskCreatedOn,
  }: {
    content: string
    author: Reference<Practitioner>
    originNode?: string
    labels?: NoteLabel['referenceString'][]
    deskCreatedOn?: Reference<DocumentReference>
  }) {
    super({
      title: 'Note',
      status: 'preliminary',
      date: now(),
      type: codeableConcept({
        system: COMPOSITION_TYPE,
        code: 'note',
        display: 'Note',
      }),
      category: [CompositionCategory.Note],
      author: [author],
      event: [
        {
          period: {
            start: today(),
            end: today(),
          },
        },
      ],
    })
    if (content) {
      this.content = content
    }
    this.originNode = originNode
    this.labels = labels
    this.deskCreatedOn = deskCreatedOn
  }

  static queryAll(): QueryTypes {
    return {
      category: CompositionCategory.Note,
      'status:not': 'entered-in-error',
      _sort: '-period',
      _count: '1000',
    }
  }

  static queryAllInPeriod({
    dateFrom,
    dateTo,
  }: {
    dateFrom?: DateString
    dateTo?: DateString
  } = {}): QueryTypes {
    // Medplum rejects comma-joined date/period prefixes (ge…,le…). Shards pass
    // only one bound per query; globalNotesListShards clips the other edge
    // client-side via minInclusive / maxExclusive. Use Composition.date, not
    // event period — practitioner access policies return zero rows on period.
    if (dateFrom) {
      return {
        ...CompositionNote.queryAll(),
        date: `ge${dateFrom}`,
      }
    }
    if (dateTo) {
      return {
        ...CompositionNote.queryAll(),
        date: `le${dateTo}`,
      }
    }
    return CompositionNote.queryAll()
  }

  static noteSearchDay(note: CompositionNote): DateString | undefined {
    const edited = note.dateString
    if (edited && edited.length >= 10) {
      return edited.slice(0, 10) as DateString
    }
    return note.period
  }

  static noteOnOrAfterDay(
    note: CompositionNote,
    minInclusive: DateString
  ): boolean {
    const day = CompositionNote.noteSearchDay(note)
    if (!day) {
      return true
    }
    return day >= minInclusive
  }

  static noteBeforeDay(
    note: CompositionNote,
    maxExclusive: DateString
  ): boolean {
    const day = CompositionNote.noteSearchDay(note)
    if (!day) {
      return true
    }
    return day < maxExclusive
  }

  // Stopgap for global /notes: Medplum caps search offset at 10k, but the page
  // still filters desk/label/content client-side, so we shard fetches by
  // calendar year (current, prior two years, then everything earlier). Extend
  // or replace with server-side search before volume blows another bucket.
  static globalNotesListShards(
    asOf: DateString = today()
  ): CompositionNoteListShard[] {
    const year = Number(asOf.slice(0, 4))
    invariant(Number.isFinite(year), 'globalNotesListShards expects YYYY-MM-DD')
    return [
      {
        query: CompositionNote.queryAllInPeriod({
          dateFrom: `${year}-01-01`,
        }),
      },
      {
        query: CompositionNote.queryAllInPeriod({
          dateTo: `${year - 1}-12-31`,
        }),
        minInclusive: `${year - 1}-01-01`,
      },
      {
        query: CompositionNote.queryAllInPeriod({
          dateTo: `${year - 2}-12-31`,
        }),
        minInclusive: `${year - 2}-01-01`,
      },
      {
        query: CompositionNote.queryAllInPeriod({
          dateTo: `${year - 3}-12-31`,
        }),
      },
    ]
  }

  static globalNotesListQueries(asOf: DateString = today()): QueryTypes[] {
    return CompositionNote.globalNotesListShards(asOf).map(
      (shard) => shard.query
    )
  }

  static queryByDesk(desk: Reference<DocumentReference>): QueryTypes {
    return {
      ...CompositionNote.queryAll(),
      _tag: `${ExtendedComposition.DESK_CREATED_ON_TAG_SYSTEM}|${desk.reference}`,
    }
  }

  /* Metadata-only listing of a desk's notes: enough to join notes to their
   * sticky-note nodes (id and the originNode extension; category/status/type
   * keep the partial resource recognizable as a note) without any body
   * leaving the server. Which notes are *visible* is decided client-side —
   * node privacy lives on the desk's flow nodes, not on the Composition — so
   * a caller resolves visibility from this index and then fetches only the
   * visible notes' content via queryByIds. */
  static queryByDeskIndex(desk: Reference<DocumentReference>): QueryTypes {
    return {
      ...CompositionNote.queryByDesk(desk),
      _elements: 'extension,category,status,type',
    }
  }

  /* Full notes by id, for the content half of the index/content split above.
   * Callers with many ids chunk (~80 per request, like the repo's other
   * comma-joined-id consumers) — `_id` grows ~37 bytes per note and the index
   * is capped at 1000, so an unchunked list can exceed the request-line
   * limit. */
  static queryByIds(ids: readonly string[]): QueryTypes {
    // `_id: ''` matches nothing while looking like a valid query; make the
    // misuse loud instead.
    invariant(ids.length > 0, 'queryByIds requires at least one id')
    return {
      _id: ids.join(','),
      _count: String(ids.length),
    }
  }

  static queryDeskNotesByIds(
    ids: readonly string[],
    { metadataOnly = false }: { metadataOnly?: boolean } = {}
  ): QueryTypes {
    const query: QueryTypes = {
      ...CompositionNote.queryAll(),
      ...CompositionNote.queryByIds(ids),
      'subject:missing': true,
    }
    if (metadataOnly) {
      query._elements = 'extension,category,status,type,event,author,subject'
    }
    return query
  }

  /* The only extension the desk list-view index reads: the node join. */
  static readonly DESK_INDEX_EXTENSION_URLS: readonly string[] = [
    COMPOSITION_NOTE_ORIGIN_NODE_SYSTEM,
  ]

  /* Defence in depth for queryByDeskIndex's `_elements=extension`, which is a
   * blanket grant: this model declares extensions that carry note text —
   * `searchText` (the full plaintext body), `aiSummary`, `aiTitle`,
   * `suggestedEditsStagingJson` (staged edit content verbatim) — and desk
   * notes carry none of them today only because the summarizer cron in
   * @seen/functions skips subject-less notes. That guarantee lives in another
   * package; this strip makes it local. Whatever the server returns, index
   * consumers see only the join extension — and `section` is dropped too, so
   * a note body cannot survive into an index entry by either route. */
  static toDeskIndexEntry(note: CompositionNote): CompositionNote {
    const resource = note.toFhirResource()
    return CompositionNote.fromFhirResource({
      ...resource,
      section: undefined,
      extension: resource.extension?.filter((extension) =>
        CompositionNote.DESK_INDEX_EXTENSION_URLS.includes(extension.url)
      ),
    })
  }

  get content(): string {
    return this.resource.section?.[0]?.text?.div ?? ''
  }

  set content(content: string) {
    /* Recomputed from the content being written, so a validated tool the
     * clinician placed in this note becomes findable from the response side and
     * one they deleted stops being. Entries naming anything else — the clinical
     * mentions filed at signing — are carried through untouched. */
    const entry = withValidatedToolEntries(
      this.resource.section?.[0]?.entry,
      content
    )
    const existingEntry = entry.length ? entry : undefined
    /* `content` is the verbatim string written to `section[0].text.div`. After
     * the Phase 2 native-note JSON migration, callers may pass either HTML or
     * a JSON.stringify'd TipTap doc. Readers (`@seen/react`'s `parseNoteContent`
     * / `contentToHTML`) detect the format at read time, so this setter is
     * intentionally format-agnostic.
     *
     * FHIR requires Narrative.div to be non-empty, and a section must contain
     * at least one of text, entry, or sub-sections. Omit the section entirely
     * when both content and entries are empty. */
    if (!content && !existingEntry?.length) {
      this.resource.section = undefined
    } else {
      const section: CompositionSection = {}
      if (content) {
        section.text = { div: content, status: 'additional' }
      }
      section.entry = existingEntry
      this.resource.section = [section]
    }
    this.resource.date = now()
  }

  get sectionEntries(): Reference[] {
    return this.resource.section?.[0]?.entry ?? []
  }

  set sectionEntries(entries: Reference[]) {
    if (!this.resource.section?.[0]) {
      this.resource.section = [{ entry: entries }]
    } else {
      this.resource.section[0].entry = entries
    }
  }

  get owner(): Reference<Practitioner> & { reference: string } {
    const author = this.resource.author?.[0]
    invariant(isOfType(author, 'Practitioner'), 'Author must be a Practitioner')
    invariant(author.reference, 'Author reference is required')
    return author as Reference<Practitioner> & { reference: string }
  }

  set owner(owner: Reference<Practitioner>) {
    this.resource.author = [owner]
  }

  get created(): DateString {
    const created = this.resource.event?.[0].period?.start
    invariant(created, 'Missing created')
    return created
  }

  /* The calendar day a note is shown under: the same value `created` and
   * `period` return, which the note header and the notes-list cell both render.
   * Editing a note later the same day must not move this to the save time.
   *
   * Unlike `created` and `period` it never throws — both reach through
   * `event?.[0].period`, which guards the array but not the element, so an
   * empty `event` is a TypeError. This yields the composition date instead,
   * for notes from sources other than the note composer that have no event
   * period at all. That fallback is a timestamp rather than a day, which is
   * the pre-existing behaviour for those notes. */
  get displayDate(): string | undefined {
    return this.resource.event?.[0]?.period?.start ?? this.dateString
  }

  set created(created: DateString) {
    this.resource.event = [
      {
        period: {
          start: created,
          end: created,
        },
      },
    ]
  }

  get period(): DateString | undefined {
    return this.resource.event?.[0]?.period?.start
  }

  set period(date: DateString | undefined | null) {
    if (!date) {
      this.resource.event = undefined
      return
    }
    this.resource.event = [
      {
        period: {
          start: date,
          end: date,
        },
      },
    ]
  }

  @Extension({
    url: COMPOSITION_NOTE_ORIGIN_NODE_SYSTEM,
    kind: 'valueString',
  })
  originNode?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_SYSTEM,
    kind: 'valueString',
  })
  aiSummary?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_GENERATED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  aiSummaryGeneratedAt?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_STARTED_AT_SYSTEM,
    kind: 'valueDateTime',
  })
  aiSummaryStartedAt?: string

  @Extension({
    url: COMPOSITION_NOTE_AI_SUMMARY_FAILED_ATTEMPTS_SYSTEM,
    kind: 'valueInteger',
  })
  aiSummaryFailedAttempts?: number

  @Extension({
    url: COMPOSITION_NOTE_AI_TITLE_SYSTEM,
    kind: 'valueString',
  })
  aiTitle?: string

  get title(): string | undefined {
    return this.aiTitle ?? this.resource.title
  }

  @Extension({
    url: COMPOSITION_NOTE_SEARCH_TEXT_SYSTEM,
    kind: 'valueString',
  })
  searchText?: string

  @Extension({
    url: SUGGESTED_EDITS_STAGING_SYSTEM,
    kind: 'valueString',
  })
  suggestedEditsStagingJson?: string

  /**
   * Parsed suggested-edits staging payload, or `null` when unset/unparseable.
   * Non-throwing: a malformed extension string (round-trip truncation, mid-PATCH
   * race) must not crash the hydration path; treat it as "no staging" and let the
   * next write heal it.
   */
  get suggestedEditsStaging(): SuggestedEditsStaging | null {
    if (!this.suggestedEditsStagingJson) {
      return null
    }
    try {
      const parsed = JSON.parse(this.suggestedEditsStagingJson)
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !Array.isArray(parsed.suggestions)
      ) {
        return null
      }
      const staging = parsed as SuggestedEditsStaging
      // Same normalization the staging `Basic` applies, so a record staged
      // under the old flat citation reads back nested from either payload.
      normalizeStagedCitations(staging)
      return staging
    } catch {
      return null
    }
  }

  set suggestedEditsStaging(staging: SuggestedEditsStaging | null) {
    this.suggestedEditsStagingJson =
      staging && staging.suggestions.length > 0
        ? JSON.stringify(staging)
        : undefined
  }

  getSuggestion(id: string): StagedSuggestion | undefined {
    return this.suggestedEditsStaging?.suggestions.find((s) => s.id === id)
  }

  /**
   * Stage a fresh batch of suggestions with RUN-SCOPED clear-then-append:
   * - KEEP decided records (`accepted`/`rejected`) so a re-hydration never
   *   re-stages a dismissed suggestion. Decided records are never auto-cleared;
   *   they persist as the accept/reject signal for training data.
   * - KEEP pending records from the SAME run as the incoming batch
   *   (`provenance.runId` equality) so an agent turn that stages multiple
   *   batches — e.g. two `editEditor` calls, or `editEditor` followed by
   *   `proposeValidatedTool` — accumulates instead of keeping only the last
   *   batch.
   * - DROP every other pending record: a NEW run replaces prior runs'
   *   suggestions (clear-and-regenerate). Records staged without a `runId`
   *   never match, so they are always replaced — the legacy clear-all
   *   behavior.
   * - Records whose id reappears in the batch are replaced, not duplicated.
   *
   * Callers must pass run-homogeneous batches (one runId per call — the run
   * key is read off `batch[0]`). An EMPTY batch drops all pendings and keeps
   * decided records.
   */
  setSuggestions(batch: StagedSuggestion[], generatedAt?: string): void {
    const batchRunId = batch[0]?.provenance.runId
    const batchIds = new Set(batch.map((s) => s.id))
    const kept =
      this.suggestedEditsStaging?.suggestions.filter(
        (s) =>
          !batchIds.has(s.id) &&
          (s.status !== 'pending' ||
            (batchRunId !== undefined && s.provenance.runId === batchRunId))
      ) ?? []
    this.suggestedEditsStaging = {
      suggestions: [...kept, ...batch],
      generatedAt: generatedAt ?? this.suggestedEditsStaging?.generatedAt,
    }
  }

  /**
   * The PENDING validated-tool suggestion for `toolName`, if any. Run-scoped
   * staging keeps at most one run's pendings alive; if that run staged the same
   * tool more than once, the latest record wins. The validated-tool node view
   * reads this (instead of hydrating a decoration) to drive its banner/prefill.
   */
  pendingValidatedToolSuggestion(
    toolName: string
  ): (StagedSuggestion & { op: PersistedValidatedToolOp }) | undefined {
    const matches = this.suggestedEditsStaging?.suggestions.filter(
      (s): s is StagedSuggestion & { op: PersistedValidatedToolOp } =>
        s.status === 'pending' &&
        isValidatedToolOp(s.op) &&
        s.op.toolName === toolName
    )
    return matches?.at(-1)
  }

  /**
   * Flip a suggestion's status, stamping `decidedAt` (ISO) when it becomes
   * `accepted` or `rejected`. No-op when the id is unknown.
   */
  setSuggestionStatus(id: string, status: StagedSuggestion['status']): void {
    const staging = this.suggestedEditsStaging
    if (!staging) {
      return
    }
    const suggestion = staging.suggestions.find((s) => s.id === id)
    if (!suggestion) {
      return
    }
    suggestion.status = status
    suggestion.decidedAt = status === 'pending' ? undefined : now()
    this.suggestedEditsStaging = staging
  }

  @Tag({
    system: NOTE_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  get labels(): NoteLabel['referenceString'][] {
    return readNoteLabelReferences(this._labels)
  }

  set labels(labels: NoteLabel['referenceString'][] | undefined) {
    this._labels = noteLabelTagCodings(labels)
  }

  /**
   * Hard-coded visit-note categories selected on this note, as `system|code`
   * tokens. Unlike `labels` (which live in `meta.tag`), categories are stored as
   * additional `Composition.category[]` codings — token-searchable server-side.
   * Only registry categories are surfaced; the Note bucket coding that
   * `isInstance`/note routing keys on is ignored here and preserved by the setter.
   */
  get categories(): string[] {
    return readVisitNoteCategoryTokens(this.resource.category)
  }

  set categories(tokens: string[] | undefined) {
    // The merge preserves the `CompositionCategory.Note` bucket coding that
    // `CompositionNote.isInstance`/note routing depend on; this setter owns ONLY
    // the registry-category entries. Drop to `undefined` when nothing remains.
    const next = mergeVisitNoteCategoryTokens(this.resource.category, tokens)
    this.resource.category = next.length > 0 ? next : undefined
  }

  @Tag({
    system: ExtendedComposition.DESK_CREATED_ON_TAG_SYSTEM,
  })
  _deskCreatedOn?: Coding

  get deskCreatedOn(): Reference<DocumentReference> | undefined {
    const code = this._deskCreatedOn?.code
    if (!code) {
      return undefined
    }
    return parseReferenceString(code) as Reference<DocumentReference>
  }

  set deskCreatedOn(desk: Reference<DocumentReference> | undefined) {
    if (!desk?.reference) {
      this._deskCreatedOn = undefined
      return
    }
    this._deskCreatedOn = {
      code: desk.reference,
      system: ExtendedComposition.DESK_CREATED_ON_TAG_SYSTEM,
    }
  }
}

/* True when a summary attempt began for the note's current content and no
 * result (success or terminal sentinel) has been written since. Both outcomes
 * stamp generatedAt after startedAt, so nothing needs to clear the started
 * stamp. */
export function hasUnfinishedAiSummaryAttempt({
  startedAt,
  generatedAt,
  contentUpdatedAt,
}: {
  startedAt: string | undefined
  generatedAt: string | undefined
  contentUpdatedAt: string | undefined
}): boolean {
  if (!startedAt) {
    return false
  }
  if (generatedAt && generatedAt >= startedAt) {
    return false
  }
  return !contentUpdatedAt || contentUpdatedAt <= startedAt
}

/* A failed summary leaves the same sentinel as an empty one, so only a note
 * carrying a failed-attempt count is ever retried. The count belongs to the
 * content the sentinel was written for: once the content changes after it, the
 * note is stale on its own and the next failure starts over at 1. */
export function nextAiSummaryFailedAttempts({
  generatedAt,
  contentUpdatedAt,
  failedAttempts,
}: {
  generatedAt: string | undefined
  contentUpdatedAt: string | undefined
  failedAttempts: number | undefined
}): number {
  const sentinelCoversContent =
    !!generatedAt && (!contentUpdatedAt || generatedAt >= contentUpdatedAt)
  return nextFailedAttemptCount(
    sentinelCoversContent ? failedAttempts : undefined
  )
}

/* True when the note's last attempt on its current content failed, retry
 * budget remains, and the failure is old enough to retry. */
export function isAiSummaryRetryDue({
  generatedAt,
  contentUpdatedAt,
  failedAttempts,
  now,
}: {
  generatedAt: string | undefined
  contentUpdatedAt: string | undefined
  failedAttempts: number | undefined
  now?: number
}): boolean {
  if (!failedAttempts || !hasAttemptBudget(failedAttempts)) {
    return false
  }
  if (isAiSummaryStale({ generatedAt, contentUpdatedAt })) {
    return false
  }
  return isRetryDue(generatedAt, now)
}

/* Shared staleness predicate. VisitNote extends Signable (not CompositionNote)
 * so the two classes can't hoist this into a common superclass, but the logic
 * is identical. */
export function isAiSummaryStale({
  generatedAt,
  contentUpdatedAt,
}: {
  generatedAt: string | undefined
  contentUpdatedAt: string | undefined
}): boolean {
  if (!generatedAt) {
    return true
  }
  if (!contentUpdatedAt) {
    return false
  }
  return generatedAt < contentUpdatedAt
}
